import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static values = {
    successMessage: { type: String, default: '' },
    errorMessage: { type: String, default: 'Failed to update' }
  }

  async submit(event) {
    const toggle = event.target
    const form = this.element

    // Add loading state
    toggle.classList.add('is-loading')

    try {
      const formData = new FormData(form)
      const response = await fetch(form.action, {
        method: form.method || 'POST',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: formData
      })

      const data = await response.json()

      if (response.ok) {
        // Update toggle state based on response
        if (typeof data.generate_page !== 'undefined') {
          toggle.checked = data.generate_page
        }

        // Show success toast
        const message = data.generate_page ?
          'Generation enabled' :
          'Generation disabled'
        this.showToast(message, 'success')

        // Update the status badge in the same row if it exists
        this.updateStatusBadge(toggle, data.generate_page)
      } else {
        // Revert toggle state on error
        toggle.checked = !toggle.checked
        this.showToast(data.error || this.errorMessageValue, 'error')
      }
    } catch (error) {
      console.error('Toggle submit error:', error)
      // Revert toggle state on error
      toggle.checked = !toggle.checked
      this.showToast(this.errorMessageValue, 'error')
    } finally {
      toggle.classList.remove('is-loading')
    }
  }

  updateStatusBadge(toggle, generatePage) {
    // Find the parent row
    const row = toggle.closest('tr') || toggle.closest('[data-service-areas-filter-target="mobileCard"]')
    if (!row) return

    const badge = row.querySelector('.badge')
    if (!badge) return

    // Only update if the page isn't already generated (Live)
    if (badge.textContent.trim().includes('Live')) return

    if (generatePage) {
      badge.className = 'badge badge-warning'
      badge.innerHTML = '<span class="w-1.5 h-1.5 rounded-full mr-1.5" style="background: currentColor;"></span>Queued'
      row.dataset.status = 'queued'
    } else {
      badge.className = 'badge badge-default'
      badge.innerHTML = '<span class="w-1.5 h-1.5 rounded-full mr-1.5" style="background: currentColor;"></span>Disabled'
      row.dataset.status = 'disabled'
    }
  }

  showToast(message, type = 'success') {
    // Create toast element
    const toast = document.createElement('div')
    toast.className = `toast toast-${type}`
    toast.style.cssText = `
      position: fixed;
      bottom: 1.5rem;
      right: 1.5rem;
      padding: 0.75rem 1rem;
      border-radius: 0.5rem;
      font-size: 0.875rem;
      font-weight: 500;
      z-index: 9999;
      animation: slideInUp 0.3s ease;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      ${type === 'success' ?
    'background: #10b981; color: white;' :
    'background: #ef4444; color: white;'}
    `

    const icon = type === 'success' ?
      '<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>' :
      '<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>'

    toast.innerHTML = `${icon}<span>${message}</span>`

    // Add animation styles if not present
    if (!document.getElementById('toast-styles')) {
      const style = document.createElement('style')
      style.id = 'toast-styles'
      style.textContent = `
        @keyframes slideInUp {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes slideOutDown {
          from { transform: translateY(0); opacity: 1; }
          to { transform: translateY(100%); opacity: 0; }
        }
      `
      document.head.appendChild(style)
    }

    document.body.appendChild(toast)

    // Remove after delay
    setTimeout(() => {
      toast.style.animation = 'slideOutDown 0.3s ease forwards'
      setTimeout(() => toast.remove(), 300)
    }, 2500)
  }
}
